import { useState } from 'react';
import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Select } from '@shared/ui/Input';
import { Table } from '@shared/ui/Table';
import { useAdminAuditQuery } from '@shared/api';

const Toolbar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const TableCard = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const Mono = styled.code`
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Details = styled.pre`
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${({ theme }) => theme.colors.text.muted};
  max-width: 400px;
  max-height: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Empty = styled.div`
  padding: ${({ theme }) => theme.spacing(8)};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
`;

function actionTone(action: string): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  if (action.includes('rate_limited') || action.includes('failed') || action.includes('disabled'))
    return 'warning';
  if (action.includes('deleted') || action.includes('error')) return 'danger';
  if (action.includes('login') || action.includes('logout')) return 'info';
  if (action.includes('created') || action.includes('enabled')) return 'success';
  return 'neutral';
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function AdminAuditTab() {
  const [limit, setLimit] = useState(50);
  const { data, isFetching } = useAdminAuditQuery({ limit }, { pollingInterval: 30_000 });
  const items = data?.items ?? [];

  return (
    <>
      <Toolbar>
        <Select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          style={{ width: 160 }}
        >
          <option value={50}>Последние 50</option>
          <option value={100}>Последние 100</option>
          <option value={200}>Последние 200</option>
          <option value={500}>Последние 500</option>
        </Select>
        <div style={{ flex: 1 }} />
        <Mono>{items.length} событий · обновление каждые 30с</Mono>
      </Toolbar>

      <TableCard>
        {isFetching && items.length === 0 ? (
          <Empty>Загрузка…</Empty>
        ) : items.length === 0 ? (
          <Empty>События отсутствуют</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <th style={{ width: 180 }}>Время</th>
                <th>Пользователь</th>
                <th>Действие</th>
                <th>Ресурс</th>
                <th>Детали</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id}>
                  <td>
                    <Mono>{formatDateTime(row.created_at)}</Mono>
                  </td>
                  <td>{row.user_email ? <Mono>{row.user_email}</Mono> : <em>system</em>}</td>
                  <td>
                    <Badge $tone={actionTone(row.action)}>{row.action}</Badge>
                  </td>
                  <td>{row.resource ? <Mono>{row.resource}</Mono> : '—'}</td>
                  <td>
                    {row.details ? <Details>{JSON.stringify(row.details, null, 0)}</Details> : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableCard>
    </>
  );
}
